'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import type { MouseEvent, MouseEventHandler } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { ArrowRight01Icon } from '@/components/ui/icon/glyphs';
import { NetworkLink } from '@/i18n/network-link';
import { MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
export type SidebarListRowIconSize = 'lg' | 'sm';
export type SidebarListRowTrailingAction = 'chevron' | 'switch' | 'none';
/** Colored status dot rendered before the trailing label (e.g. live-data state). */
export type SidebarListRowStatusTone = 'success' | 'warning' | 'destructive';
export interface SidebarListRowProps {
  icon: IconSvgElement;
  iconSize?: SidebarListRowIconSize | undefined;
  label: string;
  trailingLabel?: string | undefined;
  trailingAction?: SidebarListRowTrailingAction | undefined;
  /** When set, a colored dot precedes the trailing label. */
  statusTone?: SidebarListRowStatusTone | undefined;
  switchChecked?: boolean | undefined;
  switchDisabled?: boolean | undefined;
  disabled?: boolean | undefined;
  destructive?: boolean | undefined;
  onClick?: MouseEventHandler<HTMLElement> | undefined;
  /** Internal path (`/...`) → renders an internationalised `<Link>`. External URL (`https://...`) → renders `<a target="_blank" rel="noopener noreferrer">`. */
  href?: string | undefined;
  onAfterClick?: (() => void) | undefined;
  className?: string | undefined;
}
const ROW_BASE_CLASSES = 'inline-flex w-full min-h-11 items-center justify-start gap-3 rounded-control bg-transparent px-3 py-2 text-body font-medium text-foreground-primary transition-colors duration-fast hover:bg-background-tertiary hover:text-foreground-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-border-primary/30 motion-reduce:transition-none touch-target' as const;
function buildAriaLabel(label: string, trailingLabel?: string): string {
  return trailingLabel === undefined || trailingLabel.length === 0 ? label : `${label}, ${trailingLabel}`;
}
function isExternalHref(href: string): boolean {
  return /^[a-z]+:\/\//i.test(href);
}

// Non-interactive switch visual that mirrors `<AnimatedSwitch>`'s metrics.
// The row owns the click; the trailing slot must NOT be its own `<button>`
// (HTML disallows nested buttons). Thumb travel uses Motion with the project's
// `MOTION_POSITION_SMALL` token (matches every other small toggle in the app).
function SwitchVisual(t0) {
  const $ = _c(14);
  const {
    checked,
    disabled
  } = t0;
  const prefersReduced = useReducedMotion();
  let t1;
  if ($[0] !== prefersReduced) {
    t1 = prefersReduced ? {
      duration: 0
    } : MOTION_POSITION_SMALL;
    $[0] = prefersReduced;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const transition = t1;
  const t2 = checked ? "checked" : "unchecked";
  const t3 = checked ? "bg-button-primary-bg" : "bg-border-primary";
  const t4 = disabled && "opacity-50";
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = cn("pointer-events-none relative inline-flex h-5 w-10 shrink-0 items-center rounded-full transition-colors duration-fast motion-reduce:transition-none", t3, t4);
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = checked ? 16 : 0;
  let t7;
  if ($[5] !== t6) {
    t7 = {
      x: t6
    };
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t7 || $[8] !== transition) {
    t8 = <LazyMotion features={domAnimation}><m.span initial={false} animate={t7} transition={transition} className="pointer-events-none absolute left-0.5 block h-4 w-5 rounded-full bg-background-primary shadow-elevation-inset dark:bg-foreground-primary" /></LazyMotion>;
    $[7] = t7;
    $[8] = transition;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t2 || $[11] !== t5 || $[12] !== t8) {
    t9 = <span aria-hidden="true" data-state={t2} data-testid="sidebar-row-switch" className={t5}>{t8}</span>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
interface InnerContentProps {
  icon: IconSvgElement;
  iconSize: SidebarListRowIconSize;
  label: string;
  trailingLabel: string | undefined;
  trailingAction: SidebarListRowTrailingAction;
  statusTone: SidebarListRowStatusTone | undefined;
  switchChecked: boolean | undefined;
  switchDisabled: boolean | undefined;
  destructive: boolean;
}
function InnerContent(t0) {
  const $ = _c(29);
  const {
    icon,
    iconSize,
    label,
    trailingLabel,
    trailingAction,
    statusTone,
    switchChecked,
    switchDisabled,
    destructive
  } = t0;
  const t1 = iconSize === "sm" ? "size-4" : "size-6";
  const t2 = destructive ? "text-action-destructive-content" : "text-foreground-secondary";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("shrink-0", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const iconClasses = t3;
  const t4 = destructive ? "text-action-destructive-content" : "text-foreground-secondary";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("flex-1 truncate text-left text-body font-normal", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const labelClasses = t5;
  const hasTrailingLabel = trailingLabel !== undefined && trailingLabel.length > 0;
  let t6;
  if ($[5] !== hasTrailingLabel || $[6] !== statusTone || $[7] !== trailingLabel) {
    t6 = hasTrailingLabel ? <span className="ml-auto inline-flex max-w-40 items-center gap-1.5">{statusTone !== undefined ? <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", statusTone === "success" && "bg-feedback-success-accent", statusTone === "warning" && "bg-feedback-warning-accent", statusTone === "destructive" && "bg-feedback-error-accent")} /> : null}<span className="truncate text-body text-foreground-secondary">{trailingLabel}</span></span> : null;
    $[5] = hasTrailingLabel;
    $[6] = statusTone;
    $[7] = trailingLabel;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const trailingLabelEl = t6;
  let t7;
  if ($[9] !== icon || $[10] !== iconClasses) {
    t7 = <Icon icon={icon} aria-hidden="true" className={iconClasses} />;
    $[9] = icon;
    $[10] = iconClasses;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== label || $[13] !== labelClasses) {
    t8 = <span className={labelClasses}>{label}</span>;
    $[12] = label;
    $[13] = labelClasses;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== hasTrailingLabel || $[16] !== trailingAction) {
    t9 = trailingAction === "chevron" ? <Icon icon={ArrowRight01Icon} aria-hidden="true" className={cn("size-4 text-foreground-tertiary", !hasTrailingLabel && "ml-auto")} /> : null;
    $[15] = hasTrailingLabel;
    $[16] = trailingAction;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== hasTrailingLabel || $[19] !== switchChecked || $[20] !== switchDisabled || $[21] !== trailingAction) {
    t10 = trailingAction === "switch" ? <span className={cn("pointer-events-none", !hasTrailingLabel && "ml-auto")}><SwitchVisual checked={switchChecked ?? false} disabled={switchDisabled ?? false} /></span> : null;
    $[18] = hasTrailingLabel;
    $[19] = switchChecked;
    $[20] = switchDisabled;
    $[21] = trailingAction;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9 || $[27] !== trailingLabelEl) {
    t11 = <>{t7}{t8}{trailingLabelEl}{t9}{t10}</>;
    $[23] = t10;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = trailingLabelEl;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}
export function SidebarListRow(t0) {
  const $ = _c(42);
  const {
    icon,
    iconSize: t1,
    label,
    trailingLabel,
    trailingAction: t2,
    statusTone,
    switchChecked,
    switchDisabled,
    disabled: t3,
    destructive: t4,
    onClick,
    href,
    onAfterClick,
    className
  } = t0;
  const iconSize = t1 === undefined ? "sm" : t1;
  const trailingAction = t2 === undefined ? "chevron" : t2;
  const disabled = t3 === undefined ? false : t3;
  const destructive = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== label || $[1] !== trailingLabel) {
    t5 = buildAriaLabel(label, trailingLabel);
    $[0] = label;
    $[1] = trailingLabel;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const ariaLabel = t5;
  let composedClass;
  let handleClick;
  let inner;
  let t6;
  if ($[3] !== ariaLabel || $[4] !== className || $[5] !== destructive || $[6] !== disabled || $[7] !== href || $[8] !== icon || $[9] !== iconSize || $[10] !== label || $[11] !== onAfterClick || $[12] !== onClick || $[13] !== statusTone || $[14] !== switchChecked || $[15] !== switchDisabled || $[16] !== trailingAction || $[17] !== trailingLabel) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      composedClass = cn(ROW_BASE_CLASSES, destructive && "text-action-destructive-content", className);
      let t7;
      if ($[22] !== disabled || $[23] !== onAfterClick || $[24] !== onClick) {
        t7 = function handleClick(event) {
          if (disabled) {
            event.preventDefault();
            return;
          }
          onClick?.(event);
          onAfterClick?.();
        };
        $[22] = disabled;
        $[23] = onAfterClick;
        $[24] = onClick;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      handleClick = t7;
      let t8;
      if ($[26] !== destructive || $[27] !== icon || $[28] !== iconSize || $[29] !== label || $[30] !== statusTone || $[31] !== switchChecked || $[32] !== switchDisabled || $[33] !== trailingAction || $[34] !== trailingLabel) {
        t8 = <InnerContent icon={icon} iconSize={iconSize} label={label} trailingLabel={trailingLabel} trailingAction={trailingAction} statusTone={statusTone} switchChecked={switchChecked} switchDisabled={switchDisabled} destructive={destructive} />;
        $[26] = destructive;
        $[27] = icon;
        $[28] = iconSize;
        $[29] = label;
        $[30] = statusTone;
        $[31] = switchChecked;
        $[32] = switchDisabled;
        $[33] = trailingAction;
        $[34] = trailingLabel;
        $[35] = t8;
      } else {
        t8 = $[35];
      }
      inner = t8;
      if (href !== undefined) {
        if (isExternalHref(href)) {
          t6 = <a href={href} target="_blank" rel="noopener noreferrer" aria-label={ariaLabel} aria-disabled={disabled || undefined} className={cn(composedClass, disabled && "pointer-events-none opacity-50")} onClick={handleClick}>{inner}</a>;
          break bb0;
        }
        t6 = <NetworkLink href={href} prefetch={true} aria-label={ariaLabel} aria-disabled={disabled || undefined} className={cn(composedClass, disabled && "pointer-events-none opacity-50")} onClick={handleClick}>{inner}</NetworkLink>;
        break bb0;
      }
    }
    $[3] = ariaLabel;
    $[4] = className;
    $[5] = destructive;
    $[6] = disabled;
    $[7] = href;
    $[8] = icon;
    $[9] = iconSize;
    $[10] = label;
    $[11] = onAfterClick;
    $[12] = onClick;
    $[13] = statusTone;
    $[14] = switchChecked;
    $[15] = switchDisabled;
    $[16] = trailingAction;
    $[17] = trailingLabel;
    $[18] = composedClass;
    $[19] = handleClick;
    $[20] = inner;
    $[21] = t6;
  } else {
    composedClass = $[18];
    handleClick = $[19];
    inner = $[20];
    t6 = $[21];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[36] !== ariaLabel || $[37] !== composedClass || $[38] !== disabled || $[39] !== handleClick || $[40] !== inner) {
    t7 = <AnimatedButton variant="tertiary" disabled={disabled} disableHoverScale={true} disableTapScale={true} aria-label={ariaLabel} onClick={handleClick} className={composedClass}>{inner}</AnimatedButton>;
    $[36] = ariaLabel;
    $[37] = composedClass;
    $[38] = disabled;
    $[39] = handleClick;
    $[40] = inner;
    $[41] = t7;
  } else {
    t7 = $[41];
  }
  return t7;
}